import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Users, Zap, Crown, Play, Award, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [downloadingCert, setDownloadingCert] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lessonsRes, progressRes] = await Promise.all([
        axios.get(`${API}/lessons`),
        axios.get(`${API}/progress`),
      ]);
      setLessons(lessonsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  const handleDownloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const response = await axios.get(`${API}/certificate/generate`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const userName = (user?.dj_name || user?.name || 'DJ').replace(/\s+/g, '_');
      link.setAttribute('download', `Blendonics_Certificate_${userName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Complete the 30-Day DJ Course to get your certificate');
      } else {
        toast.error('Failed to download certificate');
      }
    } finally {
      setDownloadingCert(false);
    }
  };

  const completedLessons = progress.filter(p => p.completed).length;
  const progressPercent = lessons.length > 0 ? Math.round((completedLessons / lessons.length) * 100) : 0;

  const getTierBadge = (user) => {
    // Use account_state if available from API, otherwise compute locally
    if (user?.account_state) {
      const stateColors = {
        'Free': '#808080',
        'Starter Membership': '#D4AF37',
        'Pro Membership': '#D4AF37',
        'Elite Membership': '#D4AF37',
        'Lifetime Starter': '#22c55e'
      };
      return { 
        name: user.account_state, 
        color: stateColors[user.account_state] || '#D4AF37' 
      };
    }
    
    // Fallback for local computation
    const isLifetime = user?.has_course_access || user?.membership_status === 'lifetime';
    if (isLifetime) {
      return { name: 'Lifetime Starter', color: '#22c55e' };
    }
    
    const badges = {
      starter: { name: 'Starter Membership', color: '#D4AF37' },
      pro: { name: 'Pro Membership', color: '#D4AF37' },
      elite: { name: 'Elite Membership', color: '#D4AF37' },
    };
    return badges[user?.membership_tier] || { name: 'Free', color: '#808080' };
  };

  const tierBadge = getTierBadge(user);
  const isLifetime = user?.has_course_access || user?.membership_status === 'lifetime';

  return (
    <div className="min-h-screen py-12" data-testid="dashboard-page">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="dashboard-welcome">
            Welcome back, {user?.name}!
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-muted-foreground">Membership:</span>
            <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: tierBadge.color, color: '#141414' }} data-testid="membership-badge">
              {tierBadge.name}
            </span>
            {isLifetime && (
              <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#22c55e', color: '#ffffff' }} data-testid="lifetime-badge">
                ✓ Lifetime Access
              </span>
            )}
            {user?.has_course_access && (
              <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }} data-testid="course-badge">
                30-Day DJ Course
              </span>
            )}
            {user?.membership_status === 'inactive' && (
              <Link to="/pricing">
                <Button size="sm" variant="outline" className="ml-2" data-testid="upgrade-button">
                  Upgrade Now
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6" data-testid="stat-card-lessons">
            <div className="flex items-center justify-between mb-2">
              <BookOpen size={24} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold font-mono">{lessons.length}</span>
            </div>
            <p className="text-sm text-muted-foreground">Available Lessons</p>
          </div>

          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6" data-testid="stat-card-completed">
            <div className="flex items-center justify-between mb-2">
              <Play size={24} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold font-mono">{completedLessons}</span>
            </div>
            <p className="text-sm text-muted-foreground">Lessons Completed</p>
          </div>

          <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-6" data-testid="stat-card-progress">
            <div className="flex items-center justify-between mb-2">
              <Zap size={24} style={{ color: '#D4AF37' }} />
              <span className="text-2xl font-bold font-mono">{progressPercent}%</span>
            </div>
            <p className="text-sm text-muted-foreground">Overall Progress</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Link to="/content" data-testid="dashboard-link-content">
            <div className="group rounded-2xl border border-white/10 bg-white/5 p-8 hover:border-[#D4AF37] transition-all duration-300 cursor-pointer h-full" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <BookOpen size={32} style={{ color: '#D4AF37' }} className="mb-4" />
              <h3 className="text-xl font-bold mb-2">Content Library</h3>
              <p className="text-muted-foreground">
                Access your lessons, PDFs, SFX packs, and exclusive resources.
              </p>
            </div>
          </Link>

          <Link to="/community" data-testid="dashboard-link-community">
            <div className="group rounded-2xl border border-white/10 bg-white/5 p-8 hover:border-[#D4AF37] transition-all duration-300 cursor-pointer h-full" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <Users size={32} style={{ color: '#D4AF37' }} className="mb-4" />
              <h3 className="text-xl font-bold mb-2">Community</h3>
              <p className="text-muted-foreground">
                Connect with fellow DJs, share mixes, and get feedback.
              </p>
            </div>
          </Link>
        </div>

        {user?.membership_tier === 'elite' && (
          <Link to="/coaching" data-testid="dashboard-link-coaching">
            <div className="group rounded-2xl border p-8 hover:border-[#D4AF37] transition-all duration-300 cursor-pointer" style={{ borderColor: '#D4AF37', background: 'rgba(212, 175, 55, 0.05)', boxShadow: '0 0 30px rgba(212, 175, 55, 0.3)' }}>
              <div className="flex items-center gap-3 mb-3">
                <Crown size={32} style={{ color: '#D4AF37' }} />
                <h3 className="text-xl font-bold">Elite Coaching</h3>
              </div>
              <p className="text-muted-foreground">
                Book your monthly 1:1 coaching session and upload mixes for personalized feedback.
              </p>
            </div>
          </Link>
        )}

        {/* Certificate Download Section - for course purchasers with 95%+ progress */}
        {(user?.has_course_access || user?.membership_status === 'lifetime') && progressPercent >= 95 && (
          <div className="rounded-2xl border p-8 mb-6" style={{ borderColor: '#4B0082', background: 'linear-gradient(135deg, rgba(75, 0, 130, 0.1) 0%, rgba(212, 175, 55, 0.1) 100%)' }} data-testid="certificate-section">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.2)' }}>
                  <Award size={40} style={{ color: '#D4AF37' }} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#D4AF37' }}>Your Certificate is Ready!</h3>
                  <p className="text-muted-foreground">
                    Congratulations on completing the 30-Day DJ Beginner Course. Download your official certificate of completion.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleDownloadCertificate}
                disabled={downloadingCert}
                className="rounded-full px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #4B0082 0%, #6B238E 100%)', color: '#ffffff', fontWeight: '600' }}
                data-testid="download-certificate-btn"
              >
                <Download size={20} className="mr-2" />
                {downloadingCert ? 'Generating...' : 'Download Certificate'}
              </Button>
            </div>
          </div>
        )}

        {/* Certificate Progress Notice - shows for ALL users who haven't reached 95% */}
        {progressPercent < 95 && (
          <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: 'rgba(212, 175, 55, 0.3)', background: 'rgba(212, 175, 55, 0.05)' }} data-testid="certificate-progress-notice">
            <div className="flex items-center gap-4">
              <Award size={32} style={{ color: '#D4AF37', opacity: 0.5 }} />
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Certificate Unlocks at 95% Completion</h4>
                {(user?.has_course_access || user?.membership_status === 'lifetime') ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-2">
                      Complete {Math.max(0, Math.ceil(lessons.length * 0.95) - completedLessons)} more lesson{Math.ceil(lessons.length * 0.95) - completedLessons !== 1 ? 's' : ''} to unlock your official Blendonics certificate.
                    </p>
                    <Progress value={progressPercent} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{progressPercent}% complete • {Math.max(0, 95 - progressPercent)}% more to go</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-2">
                      Purchase the 30-Day DJ Beginner Course and complete 95% of lessons to earn your official Blendonics certificate.
                    </p>
                    <Progress value={progressPercent} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">{progressPercent}% complete • Get the course to track your progress toward certification</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {user?.membership_status === 'inactive' && (
          <div className="glass-panel rounded-2xl p-8 border text-center" data-testid="upgrade-prompt">
            <h3 className="text-2xl font-bold mb-3">Unlock Premium Content</h3>
            <p className="text-muted-foreground mb-6">
              Get access to exclusive lessons, resources, and community features with a membership.
            </p>
            <Link to="/pricing">
              <Button size="lg" className="rounded-full transition-all duration-300" style={{ background: '#D4AF37', color: '#141414', fontWeight: '600' }}>
                View Membership Plans
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;